import { Node } from '@/stores/workflowStore';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Zap, Wrench, CheckCircle, Target, Play, Bot, Plane } from 'lucide-react';
import { getBlockTypeLabel, getBlockCategoryTypeLabel } from '@/lib/blockTypes';

interface NodeInfoProps {
  node: Node;
  incomingConnections: number;
  outgoingConnections: number;
}

export function NodeInfo({ node, incomingConnections, outgoingConnections }: NodeInfoProps) {
  const typeInfo = {
    start: {
      icon: Play,
      label: getBlockCategoryTypeLabel('start'),
      description: 'Required Mission entry — every run begins here',
      color: 'bg-emerald-500/10 text-foreground border-emerald-500/40',
    },
    goal: {
      icon: Target,
      label: getBlockCategoryTypeLabel('goal'),
      description: 'Identified outcome this Mission should achieve',
      color: 'bg-primary/5 text-foreground border-primary/30',
    },
    agent: {
      icon: Bot,
      label: getBlockCategoryTypeLabel('agent'),
      description: 'Reasons and delegates in this Mission — role set by contextual configuration',
      color: 'bg-violet-500/10 text-foreground border-violet-500/40',
    },
    drone: {
      icon: Plane,
      label: getBlockCategoryTypeLabel('drone'),
      description: 'Executes remotely in this Mission — task set by contextual configuration',
      color: 'bg-sky-500/10 text-foreground border-sky-500/40',
    },
    trigger: {
      icon: Zap,
      label: getBlockCategoryTypeLabel('trigger'),
      description: 'Starts the AutoBlock Flow',
      color: 'bg-muted/50 text-foreground border-border',
    },
    tool: {
      icon: Wrench,
      label: getBlockCategoryTypeLabel('tool'),
      description: 'Processes or transforms data between blocks',
      color: 'bg-muted/50 text-foreground border-border',
    },
    action: {
      icon: CheckCircle,
      label: getBlockCategoryTypeLabel('action'),
      description: 'Performs an operation or sends data',
      color: 'bg-muted/50 text-foreground border-border',
    },
  };

  const info = typeInfo[node.type as keyof typeof typeInfo] ?? typeInfo.tool;
  const Icon = info.icon;

  const getConfigSummary = () => {
    const { config } = node;
    const items: string[] = [];
    const asText = (value: unknown) =>
      typeof value === 'string' ? value : value != null ? JSON.stringify(value) : '';

    const url = asText(config.url);
    if (url) items.push(`URL: ${url.length > 30 ? `${url.substring(0, 30)}...` : url}`);
    if (config.method) items.push(`Method: ${String(config.method)}`);
    if (config.name) items.push(`Name: ${String(config.name)}`);
    const purpose = asText(config.contextPurpose);
    if (purpose) items.push(`Purpose: ${purpose.length > 40 ? `${purpose.substring(0, 40)}...` : purpose}`);
    const outcome = asText(config.outcome);
    if (outcome) items.push(`Outcome: ${outcome.length > 40 ? `${outcome.substring(0, 40)}...` : outcome}`);
    const message = asText(config.message);
    if (message) items.push(`Message: ${message.length > 30 ? `${message.substring(0, 30)}...` : message}`);
    if (config.interval) items.push(`Interval: ${String(config.interval)}s`);
    if (config.duration) items.push(`Duration: ${String(config.duration)}ms`);
    const condition = asText(config.condition);
    if (condition) items.push(`Condition: ${condition.length > 30 ? `${condition.substring(0, 30)}...` : condition}`);
    if (config.key) items.push(`Key: ${String(config.key)}`);

    return items;
  };

  return (
    <Card className={`p-4 space-y-4 ${info.color} border-2`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">{getBlockTypeLabel(node.type, node.subtype)}</p>
            <p className="text-xs opacity-75 mt-1">{info.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-medium opacity-75">Configuration:</p>
        {getConfigSummary().length > 0 ? (
          <div className="space-y-1">
            {getConfigSummary().map((item, idx) => (
              <p key={idx} className="text-xs opacity-75 font-mono">
                {item}
              </p>
            ))}
          </div>
        ) : (
          <p className="text-xs opacity-50 italic">No configuration yet</p>
        )}
      </div>

      <div className="flex gap-4 pt-2 border-t border-current/20">
        <div>
          <p className="text-xs opacity-75">Input From</p>
          <p className="text-sm font-semibold">{incomingConnections}</p>
        </div>
        <div>
          <p className="text-xs opacity-75">Output To</p>
          <p className="text-sm font-semibold">{outgoingConnections}</p>
        </div>
      </div>
    </Card>
  );
}
